import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useMemo, useEffect } from 'react';
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Tabs,
  message,
  DatePicker,
  Form,
  Tooltip,
  Drawer,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useFetchAllTransferts, useValidateTransfert, useUnvalidateTransfert } from '../../hooks/transfertHooks';
import TransfertForm from './TransfertForm';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// Constants for localStorage
const ACTIVE_TAB_KEY = 'transfert_active_tab';

const TransfertList = ({ onSelect, userPermissions = { isManager: false } }) => {
  const navigate = useNavigate();
  const [refSearch, setRefSearch] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  // Initialize activeTab from localStorage or default to 'notValidated'
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem(ACTIVE_TAB_KEY);
    return savedTab || 'notValidated';
  });

  // Initialize sorter based on activeTab
  const [sorter, setSorter] = useState(() => {
    return {
      field: 'REF_TRF',
      order: 'ascend'
    };
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [form] = Form.useForm();
  const [hoveredRow, setHoveredRow] = useState(null);

  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
  }, [activeTab]);

  const params = useMemo(() => ({
    page: pagination.current,
    pageSize: pagination.pageSize,
    refSearch,
    dateFrom: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    dateTo: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    sortField: sorter.field,
    sortOrder: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined,
    status: activeTab,
  }), [pagination.current, pagination.pageSize, refSearch, dateRange, sorter.field, sorter.order, activeTab]);

  const { items, total, loading, error, refetch } = useFetchAllTransferts(params);
  const { validate } = useValidateTransfert();
  const { unvalidate } = useUnvalidateTransfert();

  // Handle row click
  const handleRowClick = (record) => {
    onSelect(record.id);
  };

  const referenceColumn = {
    title: 'Ref Transfert',
    dataIndex: 'REF_TRF',
    key: 'REF_TRF',
    sorter: true,
    defaultSortOrder: 'ascend',
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Commence par"
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            setRefSearch(selectedKeys[0]);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          style={{ marginBottom: 8, display: 'block' }}
          ref={node => node && setTimeout(() => node.focus(), 0)}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              setRefSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Rechercher
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setRefSearch('');
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            size="small"
            style={{ width: 90 }}
          >
            Réinitialiser
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    render: (text, record) => (
      <span
        style={{
          position: 'relative',
          fontWeight: hoveredRow === record.id ? 'bold' : 'normal',
          transition: 'font-weight 0.3s ease'
        }}
      >
        {text}
        <span
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: hoveredRow === record.id ? '100%' : '0%',
            height: '1px',
            backgroundColor: '#000',
            transition: 'width 0.3s ease'
          }}
        />
      </span>
    )
  };

  const dateColumn = {
    title: activeTab === 'notValidated' ? 'Date Transfert' : 'Date Valid.',
    dataIndex: 'DATE_TRF',
    key: 'date',
    render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '',
    sorter: true,
    onCell: () => ({
      onClick: e => {
        e.stopPropagation();
        return false;
      }
    })
  };

  const sourceEntrepotColumn = {
    title: 'Entrepôt Source',
    dataIndex: ['entrepotSource', 'name'],
    key: 'source',
    render: (text, record) => `${record.entrepotSource?.code_ent || ''} - ${text || ''}`,
  };

  const destEntrepotColumn = {
    title: 'Entrepôt Destination',
    dataIndex: ['entrepotDest', 'name'],
    key: 'dest',
    render: (text, record) => `${record.entrepotDest?.code_ent || ''} - ${text || ''}`,
  };

  const handleValidate = async (record) => {
    try {
      await validate(record.id);
      message.success('Transfert validé avec succès');
      refetch();
    } catch (e) {
      message.error(`Échec de la validation: ${e.message || e}`);
    }
  };

  const handleUnvalidate = async (record) => {
    try {
      await unvalidate(record.id);
      message.success('Transfert dévalidé avec succès');
      refetch();
    } catch (e) {
      message.error(`Échec de la dévalidation: ${e.message || e}`);
    }
  };

  const columns = [
    dateColumn,
    referenceColumn,
    sourceEntrepotColumn,
    destEntrepotColumn,
    {
      title: 'Remarque',
      dataIndex: 'REMARQUE',
      key: 'REMARQUE',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => {
        const canValidate = activeTab === 'notValidated';
        const canUnvalidate = activeTab === 'validated';
        return (
          <Space>
            {canValidate && (
              <PermissionButton permissionModule="transfers" permissionAction="validate"
                type="primary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleValidate(record);
                }}
              >
                Valider
              </PermissionButton>
            )}
            {canUnvalidate && (
              <PermissionButton permissionModule="transfers" permissionAction="unvalidate"
                type="default"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnvalidate(record);
                }}
              >
                Dévalider
              </PermissionButton>
            )}
          </Space>
        );
      },
      onCell: () => ({
        onClick: e => e.stopPropagation(),
      }),
    },
  ];

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSorter({
      field: 'REF_TRF',
      order: 'ascend'
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    setSorter(sorter);
  };

  const getRowStyle = (record) => {
    if (activeTab === 'validated') {
      return { backgroundColor: '#f6ffed' };
    }
    return {};
  };

  const handleDateChange = (dates) => {
    setDateRange(dates || [null, null]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearAllFilters = () => {
    setRefSearch('');
    setDateRange([null, null]);
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleRefetch = async () => {
    try {
      await refetch();
      message.success('Liste des transferts actualisée');
    } catch (error) {
      message.error(`Échec de l'actualisation: ${error?.message || 'Erreur inconnue'}`);
    }
  };

  return (
    <div className="transfert-list-container">
      <Card
        style={{
          marginTop: 20,
          backgroundColor: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
        className="transfert-card"
      >
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/article')}>
              Retour
            </Button>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', margin: 0 }}>Liste Des Transferts</h2>
            <Tooltip title="Actualiser la liste">
              <Button
                onClick={handleRefetch}
                icon={<ReloadOutlined />}
                type="text"
              />
            </Tooltip>
          </div>
          <Space>
            <PermissionButton permissionModule="transfers" permissionAction="create"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsDrawerOpen(true)}
            >
              Créer Transfert
            </PermissionButton>
          </Space>
        </Space>

        <Form
          form={form}
          layout="inline"
          style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}
        >
          <Form.Item
            name="dateRange"
            label="Période"
            style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6, marginBottom: 4 }}
            labelCol={{ style: { display: 'flex', alignItems: 'center', paddingBottom: 0 } }}
          >
            <RangePicker
              onChange={handleDateChange}
              value={dateRange}
              format="DD/MM/YYYY"
              placeholder={['Date début', 'Date fin']}
              allowClear
              size="small"
              style={{ width: 260 }}
            />
          </Form.Item>
        </Form>

        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Enregistrés" key="notValidated" />
          <TabPane tab="Validés" key="validated" />
        </Tabs>

        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            onMouseEnter: () => setHoveredRow(record.id),
            onMouseLeave: () => setHoveredRow(null),
            style: { ...getRowStyle(record), cursor: 'pointer' },
            className: 'transfert-table-row',
          })}
          className="transfert-table"
          size="small"
        />

        <Drawer
          title="Créer Nouveau Transfert"
          width={920}
          onClose={() => setIsDrawerOpen(false)}
          open={isDrawerOpen}
          bodyStyle={{ paddingBottom: 80 }}
        >
          <TransfertForm
            onTransfertCreated={(id) => {
              setIsDrawerOpen(false);
              onSelect(id);
            }}
            onCancel={() => setIsDrawerOpen(false)}
          />
        </Drawer>
      </Card>
    </div>
  );
};

export default TransfertList;

